/**
 * subscriptionAdapter — `UserResource` / `UserSubscriptionResource` → les formes du portail.
 *
 * Port littéral de ce que `ParentSubscriptionPortalScreen.js` dérive dans son corps : le libellé
 * d'offre (`CURRENT_OFFER_LABELS:86-100`), le volume hebdomadaire (`getHoursPerWeekLabel:102-105`),
 * le nom de statut (`subscriptionStatusNames`, `MySubscriptionScreen.js:20-36`), les chips
 * d'objectifs (`objectiveChips:796-829`) et les lignes datées de la carte d'état (`:986-1087`).
 *
 * Les six prédicats de statut (`isSubscribed`, `isSubscriptionRecurring`, …) sont eux aussi
 * recopiés depuis `data/utility/dataUtils.js`, alors que `src/data/**` est pourtant autorisé aux
 * ports : ce module-là tire `common/theme/theme` et un écran de `src/student/**`, donc l'importer
 * ferait entrer tout le graphe legacy dans un test d'adaptateur. Trois listes de statuts et trois
 * égalités, citées ligne à ligne, coûtent moins cher que cette dépendance.
 *
 * Toutes les dates et tous les montants sortent d'ici **déjà en toutes lettres** : le design
 * system ne calcule rien.
 */
import moment from "moment"

import { type ApiSchoolSubject, indexSubjectsById, toUISubject } from "../shared/schoolSubjectAdapter"
import { allocationBySubject } from "../shared/activationDisplay"
import { formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { eurosCents } from "../../../ui/formatters/moneyFormatter"
import { type UISubscriptionLine, type UISubscriptionObjective } from "../../../ui/types"

// ── Formes lâches : ces charges viennent de réducteurs en JS, tout est optionnel ──────────

export type ApiSubscriptionState = {
    status?: string
    plan?: string
    planned_plan?: string
    hours_per_month?: number
    end_date?: string
    planned_start_date?: string
    is_manual_sub?: boolean
}

export type ApiPedagogicalObjective = {
    id?: string | number
    status?: string
    subject_ids?: (string | number)[]
    allocated_hours?: number
}

export type ApiPlannedActivation = {
    effective_at?: string
    offer_label?: string
    hours_per_week?: number
    monthly_price?: number
    first_month_price?: number
    subject_ids?: (string | number)[]
    hours_allocation?: unknown
    resume_slots?: unknown
    loyalty_discount_installments?: number
    loyalty_discount_total?: number
    modifiable?: boolean
    is_past_due?: boolean
}

export type ApiRecommendedOffer = {
    offer_label?: string
    hours_per_week?: number
    monthly_price?: number
    first_month_price?: number
    loyalty_eligible?: boolean
}

export type ApiUpcomingActivation = {
    type?: string
    held_in_year?: boolean
    eligible?: boolean
    due_date?: string
    resume_date?: string
    extendable_to?: string
    discount_expires_at?: string
    planned?: ApiPlannedActivation | null
    recommended_offer?: ApiRecommendedOffer | null
}

export type ApiPortalUser = {
    id?: string | number
    firstname?: string
    email?: string
    /** Le fuseau de la famille (détection geoip) — il décale l'AFFICHAGE des créneaux, jamais leur valeur. */
    timezone?: string | null
    current_offer?: string
    impersonation?: boolean
    /** Les blocs éteints dans l'espace de l'élève (lus par `isBlockOn`). Absent = rien d'éteint. */
    disabled_blocks?: string[]
    payment_collection_day?: number
    /** Le jour de fin de mois que seule l'équipe accorde : l'espace parent ne le change pas. */
    payment_collection_day_granted?: boolean
    commitment_ends_at?: string
    has_summer_fast_track?: boolean
    summer_summer_status?: string
    summer_resume_date?: string
    subscription_state?: ApiSubscriptionState
    upcoming_invoice_data?: { amount_due?: number; next_payment_attempt?: string }
    /** Le solde client Stripe, en euros, signé : négatif = crédit de la famille. */
    stripe_balance?: number | null
    planned_subscription?: { start_date?: string }
    pedagogical_objectives?: ApiPedagogicalObjective[]
    upcoming_activation?: ApiUpcomingActivation | null
}

export type ApiPauseBalance = {
    current_pause?: {
        reason?: string
        is_cancellable?: boolean
        resume_date?: string
        previous_commitment_end_date?: string
        applied_credit?: number
    } | null
}

// ── Libellés ──────────────────────────────────────────────────────────────────────────────

/**
 * `current_offer` → libellé français de la ligne d'offre. Miroir de
 * `SubscriptionHelperV2::getOfferName`, gardé côté UI pour éviter un aller-retour.
 */
export const CURRENT_OFFER_LABELS: Record<string, string> = {
    integral: "Cours particuliers",
    essential: "Cours partagés",
    autonomy: "Mode Autonome",
    initial: "Initiale",
    plus: "Plus",
    premium: "Premium",
    // Produits d'appel saisonniers : sans ces entrées, le repli nommait l'offre d'une famille
    // en cours d'été par le nom d'une offre qu'elle n'a pas.
    cours_ete: "Cours d'été",
    holidaycourse: "Stage",
    holiday_course: "Stage",
    stage: "Stage",
}

/**
 * Repli « Cours partagés » : un slug inconnu se nomme mieux ainsi que par une ligne de volume seule.
 * Le portail ne le lit que pour une famille qui tient un abonnement, jamais pour un abonnement
 * terminé. Une pause retire pourtant le rôle d'abonné : une famille en pause lit `basic`, donc
 * « Cours partagés » quelle que soit son offre — un défaut connu, que ce repli ne corrige pas.
 */
export function offerLabelFor(currentOffer: string | null | undefined): string {
    return CURRENT_OFFER_LABELS[currentOffer ?? ""] ?? "Cours partagés"
}

/** Grille `hours_per_month` → cadence hebdomadaire (miroir de `SubscriptionHelperV2::SOLD_CADENCE`). */
export const HOURS_PER_MONTH_TO_PER_WEEK: Record<number, number> = {
    4: 1,
    6: 1.5,
    9: 2,
    13: 3,
    17: 4,
    22: 5,
    26: 6,
    30: 7,
    35: 8,
    39: 9,
    43: 10,
}

export function weeklyHoursFromMonthly(hoursPerMonth: number | null | undefined): number {
    if (!hoursPerMonth || hoursPerMonth <= 0) return 0
    return HOURS_PER_MONTH_TO_PER_WEEK[hoursPerMonth] ?? Math.round((hoursPerMonth / 4.35) * 10) / 10
}

/** « 2h/sem », ou `null` quand la grille ne donne rien. */
export function hoursPerWeekLabel(hoursPerMonth: number | null | undefined): string | null {
    const weekly = weeklyHoursFromMonthly(hoursPerMonth)
    return weekly > 0 ? `${weekly}h/sem` : null
}

/** `subscriptionStatusNames` de `MySubscriptionScreen.js:20-36`. */
export const SUBSCRIPTION_STATUS_NAMES: Record<string, string> = {
    recurring: "Abonnement actif",
    past_due: "Abonnement actif - en retard de paiement",
    on_grace_period: "Abonnement actif jusqu'à échéance",
    on_trial: "Essai gratuit en cours",
    on_generic_trial: "Essai gratuit en cours",
    on_grace_period_before_pause: "Mise en pause planifiée",
    paused: "Abonnement mis en pause",
    planned: "Activation planifiée",
    // Sans cette clé, tout élève portant une évolution planifiée affichait « Pas d'abonnement actif ».
    planned_subscription_update: "Abonnement actif - changement planifié",
    expired_trial: "Essai gratuit expiré",
    ended: "Abonnement expiré",
}

/** Le nom d'un statut connu, `null` sinon. */
export function statusNameFor(status: string | null | undefined): string | null {
    return SUBSCRIPTION_STATUS_NAMES[status ?? ""] ?? null
}

export function statusLabelFor(status: string | null | undefined): string {
    return statusNameFor(status) ?? "Pas d'abonnement actif"
}

// ── Prédicats de statut (recopie de `data/utility/dataUtils.js`) ──────────────────────────

/** `subscribedStatuses` — « cette famille est abonnée et paie » (`dataUtils.js:30-38`). */
export const SUBSCRIBED_STATUSES = [
    "recurring",
    "on_trial",
    "on_grace_period",
    "on_grace_period_before_pause",
    "planned",
    "past_due",
    "planned_subscription_update",
]

/** `subscriptionPausedStatuses` (`dataUtils.js:6-10`). */
export const SUBSCRIPTION_PAUSED_STATUSES = ["on_grace_period_before_pause", "paused", "planned"]

const statusOf = (user: ApiPortalUser | null | undefined) => user?.subscription_state?.status ?? ""

/** Un abonnement terminé, un essai expiré, ou aucun abonnement jamais pris (`-`). */
export const SUBSCRIPTION_OVER_STATUSES = ["ended", "expired_trial", "-"]

export const isSubscriptionOver = (user: ApiPortalUser | null | undefined) =>
    SUBSCRIPTION_OVER_STATUSES.includes(statusOf(user))

/** La famille tient un abonnement : il court, il est en pause ou il est planifié. */
export const holdsSubscription = (user: ApiPortalUser | null | undefined) =>
    statusOf(user) !== "" && !isSubscriptionOver(user)

export const isSubscribed = (user: ApiPortalUser | null | undefined) => SUBSCRIBED_STATUSES.includes(statusOf(user))
export const isSubscriptionPaused = (user: ApiPortalUser | null | undefined) =>
    SUBSCRIPTION_PAUSED_STATUSES.includes(statusOf(user))
export const isSubscriptionRecurring = (user: ApiPortalUser | null | undefined) =>
    statusOf(user) === "recurring" || statusOf(user) === "past_due" || statusOf(user) === "on_grace_period"
export const isSubscriptionTrialing = (user: ApiPortalUser | null | undefined) => statusOf(user) === "on_trial"
export const isPausePlanned = (user: ApiPortalUser | null | undefined) =>
    statusOf(user) === "on_grace_period_before_pause"
export const isPauseActive = (user: ApiPortalUser | null | undefined) => statusOf(user) === "paused"

// ── Chips d'objectifs ─────────────────────────────────────────────────────────────────────

/**
 * UNE chip par OBJECTIF (jamais par matière), pour que la somme des heures affichées reste égale
 * aux heures souscrites. Les objectifs `closed` sont écartés, et un objectif sans matière ni
 * volume est sauté — comme le legacy, qui rendait `null` puis filtrait.
 */
export function toSubscriptionObjectives(
    objectives: ApiPedagogicalObjective[] | null | undefined,
    schoolSubjects: ApiSchoolSubject[] | null | undefined
): UISubscriptionObjective[] {
    const byId = indexSubjectsById(schoolSubjects)

    return (Array.isArray(objectives) ? objectives : [])
        .filter((objective) => objective && objective.status !== "closed")
        .map((objective, index): UISubscriptionObjective | null => {
            const subjectIds = Array.isArray(objective?.subject_ids) ? objective.subject_ids : []
            const hours = objective?.allocated_hours ?? null
            const hasHours = !!hours && hours > 0

            const subjects = subjectIds
                .map((id) => byId[String(id)])
                .filter(Boolean)
                .map(toUISubject)

            if (subjects.length === 0 && !hasHours) return null

            return { id: objective?.id ?? `obj-${index}`, subjects, hours: hasHours ? hours : null }
        })
        .filter((chip): chip is UISubscriptionObjective => chip != null)
}

/**
 * Matières de la reprise planifiée — UNE chip par MATIÈRE cette fois (la suite n'a pas
 * commencé, il n'y a pas encore d'objectif à grouper). Port de `getSubjectHoursChips`.
 * Une matière que le référentiel ne résout pas est omise, jamais affichée en identifiant.
 */
export function toPlannedSubjectObjectives(
    subjectIds: (string | number)[] | null | undefined,
    hoursAllocation: unknown,
    schoolSubjects: ApiSchoolSubject[] | null | undefined
): UISubscriptionObjective[] {
    if (!Array.isArray(subjectIds) || subjectIds.length === 0) return []
    const byId = indexSubjectsById(schoolSubjects)
    const allocation = allocationBySubject((hoursAllocation ?? {}) as Parameters<typeof allocationBySubject>[0])

    return subjectIds
        .map((id): UISubscriptionObjective | null => {
            const api = byId[String(id)]
            if (!api?.name) return null
            const hours = allocation[String(id)]

            return { id: `planned-subject-${id}`, subjects: [toUISubject(api)], hours: hours > 0 ? hours : null }
        })
        .filter((chip): chip is UISubscriptionObjective => chip != null)
}

// ── Lignes datées de la carte d'état ──────────────────────────────────────────────────────

/**
 * Les quatre lignes datées, dans l'ordre exact du legacy (`:986-1087`) : prochain paiement,
 * mise en pause, passage / reprise planifié, fin d'engagement.
 *
 * `hideCommitmentEnd` porte la garde d'été (`hideCommitmentEndLine:342-347`) : quand l'échéance
 * tombe dans la fenêtre gérée par le cockpit, c'est la narration cockpit qui la dit.
 */
export function toSubscriptionStateLines(
    user: ApiPortalUser | null | undefined,
    options: { hideCommitmentEnd?: boolean } = {}
): UISubscriptionLine[] {
    const { hideCommitmentEnd = false } = options
    const lines: UISubscriptionLine[] = []
    const state = user?.subscription_state

    const amountDue = user?.upcoming_invoice_data?.amount_due
    const nextPaymentAmount = amountDue != null && amountDue > 0 ? amountDue : null
    const nextPaymentAttempt =
        user?.upcoming_invoice_data?.next_payment_attempt ?? user?.planned_subscription?.start_date

    if (nextPaymentAmount != null) {
        lines.push({
            icon: "file-invoice-dollar",
            // Sans date, le legacy laisse `moment(undefined)` rendre aujourd'hui
            // (`ParentSubscriptionPortalScreen.js:1011`) : on n'invente pas de repli à sa place.
            text: `Prochain paiement : ${nextPaymentAmount}€ le ${formatDayMonthYear(
                nextPaymentAttempt ?? moment().toISOString()
            )}`,
        })
    }

    // Le crédit déjà déduit du prochain paiement ci-dessus, dit en clair : sans lui, la famille ne le
    // lit nulle part.
    const balance = user?.stripe_balance
    if (balance != null && balance < 0) {
        lines.push({
            icon: "hand-holding-usd",
            text: `Crédit : ${eurosCents(Math.round(-balance * 100))}, déduit de vos prochains paiements`,
        })
    }

    if (isPausePlanned(user) && state?.end_date) {
        lines.push({ icon: "pause", text: `Mise en pause le ${formatDayMonthYear(state.end_date)}` })
    }

    if (state?.planned_start_date) {
        lines.push({
            icon: "arrow-right",
            text: isSubscriptionPaused(user)
                ? `Reprise en ${state.planned_plan} prévue le ${formatDayMonthYear(state.planned_start_date)}`
                : `Passage en ${state.planned_plan} prévu le ${formatDayMonthYear(state.planned_start_date)}`,
        })
    }

    const commitmentEndsAt = user?.commitment_ends_at
    if (commitmentEndsAt && moment(commitmentEndsAt).isAfter(moment()) && !hideCommitmentEnd) {
        lines.push({ icon: "handshake", text: `Fin d'engagement : le ${formatDayMonthYear(commitmentEndsAt)}` })
    }

    return lines
}
